import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { ArrowRight, ShieldCheck } from 'lucide-react';

const Welcome = () => {
  const navigate = useNavigate();
  const [showSplash, setShowSplash] = useState(true);

  // Efecto para el Loading Inicial (Splash Screen)
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSplash(false);
    }, 2500); // 2.5 segundos para apreciar bien el efecto
    return () => clearTimeout(timer);
  }, []);

  return (
    <Layout showNavigation={false}>
      {/* LayoutGroup permite que la magia de que el logo "vuele" de un lugar a otro funcione */}
      <LayoutGroup>
        
        <AnimatePresence mode="wait">
          {/* =========================================
              1. PANTALLA DE CARGA (SPLASH SCREEN)
              ========================================= */}
          {showSplash ? (
            <motion.div
              key="splash"
              exit={{ opacity: 0, backdropFilter: "blur(10px)" }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
              className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-gradient-to-br from-blue-900 to-[#0a1930]"
            >
              {/* 🔥 ESTE ES TU "LOGO 2" (El ícono centrado) */}
              <motion.img
                layoutId="logo-mark" // ESTA ES LA PALABRA MÁGICA
                src="/logo-icono.png" // RUTA A TU ÍCONO RECORTADO
                alt="Logo Icono"
                className="w-32 h-32 md:w-40 md:h-40 object-contain shadow-2xl rounded-[32px] mb-8"
                animate={{ 
                  scale: [1, 1.05, 1],
                  boxShadow: [
                    "0px 0px 0px 0px rgba(59, 130, 246, 0)",
                    "0px 0px 40px 10px rgba(59, 130, 246, 0.4)",
                    "0px 0px 0px 0px rgba(59, 130, 246, 0)"
                  ]
                }}
                transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
              />
              
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex flex-col items-center"
              >
                <div className="w-8 h-8 border-4 border-blue-400/30 border-t-white rounded-full animate-spin mb-4"></div>
                <p className="text-blue-200/80 text-[11px] font-bold uppercase tracking-widest">Iniciando plataforma...</p>
              </motion.div>
            </motion.div>
          ) : (
            /* =========================================
                2. PANTALLA PRINCIPAL DE BIENVENIDA
                ========================================= */
            <motion.div
              key="main"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="min-h-screen bg-[#f4f7fb] flex flex-col items-center justify-center px-5 py-10 relative overflow-hidden"
            >
              {/* Decoraciones de fondo modernas */}
              <div className="absolute top-[-10%] left-[-10%] w-72 md:w-96 h-72 md:h-96 bg-blue-400/20 rounded-full blur-3xl pointer-events-none"></div>
              <div className="absolute bottom-[-10%] right-[-10%] w-72 md:w-96 h-72 md:h-96 bg-indigo-400/20 rounded-full blur-3xl pointer-events-none"></div>

              <motion.div
                className="w-full max-w-md md:max-w-lg flex flex-col items-center text-center relative z-10"
                initial={{ y: 30 }}
                animate={{ y: 0 }}
                transition={{ delay: 0.2, duration: 0.8, ease: 'easeOut' }}
              >
                
                {/* 🔥 TARJETA DEL LOGO ARMADO CON TAMAÑO MAYOR EN PC */}
                <div className="bg-gradient-to-r from-blue-900 to-blue-700 px-6 py-4 md:px-8 md:py-5 rounded-[28px] md:rounded-[32px] shadow-[0_15px_30px_rgba(30,58,138,0.25)] mb-8 md:mb-10 transform hover:scale-105 transition-transform duration-300 flex items-center gap-4 md:gap-5">
                  
                  {/* EL LOGO 2 ATERRIZA AQUÍ (Aumentado a w-20 h-20 en md) */}
                  <motion.img
                    layoutId="logo-mark" // LA MISMA PALABRA MÁGICA CONECTA AMBOS ESTADOS
                    src="/logo-icono.png" 
                    alt="Logo Icono"
                    className="w-20 h-20 md:w-20 md:h-20 object-contain rounded-xl"
                  />
                  
                  {/* TEXTO HTML (Aumentado a text-4xl en md) */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                    className="flex flex-col text-left"
                  >
                    <span className="text-4xl md:text-4xl font-black tracking-tight leading-none text-white pb-0.5">
                      Remesas
                    </span>
                    <span className="text-4xl md:text-4xl font-black tracking-tight leading-none text-blue-200">
                      Venezuela
                    </span>
                  </motion.div>
                </div>

                {/* Insignia de Confianza */}
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.6 }}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full border border-blue-100 mb-6 shadow-sm"
                >
                  <ShieldCheck className="w-4 h-4" />
                  <span className="text-[10px] font-black uppercase tracking-widest">100% Seguro y Confiable</span>
                </motion.div>

                {/* Títulos */}
                <motion.h1 
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
                  className="text-4xl md:text-[46px] font-black text-slate-800 mb-4 tracking-tight leading-[1.1]"
                >
                  Envía dinero sin <br/> complicaciones
                </motion.h1>

                <motion.p 
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}
                  className="text-[15px] md:text-base text-slate-500 mb-12 max-w-[280px] md:max-w-sm leading-relaxed font-medium"
                >
                  La forma más rápida y económica de conectar con tus seres queridos en toda América Latina.
                </motion.p>

                {/* Botones de Acción */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}
                  className="w-full space-y-4 max-w-[320px]"
                >
                  <button
                    onClick={() => navigate('/register')}
                    className="w-full flex items-center justify-center gap-3 bg-blue-600 text-white py-4 px-8 rounded-2xl font-black text-sm uppercase tracking-widest shadow-[0_10px_20px_rgba(37,99,235,0.3)] hover:shadow-[0_15px_25px_rgba(37,99,235,0.4)] hover:bg-blue-700 active:scale-[0.98] transition-all duration-300 group"
                  >
                    Crear mi Cuenta
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>

                  <button
                    onClick={() => navigate('/login')}
                    className="w-full bg-white text-blue-900 py-4 px-8 rounded-2xl font-black text-sm uppercase tracking-widest border border-slate-200 shadow-sm hover:bg-slate-50 hover:border-blue-200 active:scale-[0.98] transition-all duration-300"
                  >
                    Ya tengo Cuenta
                  </button>
                </motion.div>

                {/* Footer Text */}
                <motion.p 
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}
                  className="mt-12 text-[10px] font-bold text-slate-400 uppercase tracking-widest max-w-[250px] leading-relaxed"
                >
                  © {new Date().getFullYear()} Remesas Venezuela <br/>
                  Plataforma de envíos internacionales.
                </motion.p>
                
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </LayoutGroup>
    </Layout>
  );
};

export default Welcome;

